import { c as _c } from "react/compiler-runtime";
import { Form, Formik, useFormikContext } from 'formik';
import { useContext, useEffect, useState } from 'react';
import { DashboardContext } from '../../DashboardContext';
import SwitchInput from '../../../../common/Form/Fields/SwitchInput';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import { Div } from '../../../../common/helpers/StyledUtils';
import Text from '../../../../common/base/Text';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { UserContext } from '../../../../contexts/UserContext';
import useSWR from 'swr';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import Button from '../../../../common/base/Button';
import { capitalizeFirstLetter } from '../../../../../utils';
import Link from '../../../../common/base/Link';
import { WithTeamAdminRole } from '../../../../common/WithRole';
import Icon from '../../../../common/base/Icon';
import DropdownMenu from '../../../../common/DropdownMenu';
import { team_levels_by_role } from '../../../../../../../../domain/roles';
import { TeamRole } from '../../../../../../../../typings/TeamMember.interface';
import { useDialog } from '../../../../common/Dialog';
import { Route } from 'react-router';
import AddTeamMemberModal from './AddTeamMemberModal';
import Container from '../../../../common/base/Container';
import Tooltip from '../../../../common/base/Tooltip';
const description_by_role = {
  admin: 'Unrestricted access to this project & can manage members',
  member: 'Read and write access to this project, can only view project members',
  viewer: 'Read-only access to this project, can only view project members'
};
export const createTeamRoleOptions = (viewer_enabled: boolean) => Object.keys(description_by_role).map((role: TeamRole) => ({
  value: role,
  label: `Project ${capitalizeFirstLetter(role)}`,
  description: role === 'viewer' && !viewer_enabled ? 'Upgrade plan to enable role' : description_by_role[role],
  disabled: role === 'viewer' && !viewer_enabled,
  tooltip: role === 'viewer' && !viewer_enabled ? 'Upgrade plan to enable role' : undefined
}));
const SubmitOnchange = () => {
  const $ = _c(4);
  const formik = useFormikContext();
  let t0;
  if ($[0] !== formik) {
    t0 = () => {
      if (formik.dirty) {
        formik.submitForm();
        formik.resetForm({
          values: formik.values
        });
      }
    };
    $[0] = formik;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== formik.dirty) {
    t1 = [formik.dirty];
    $[2] = formik.dirty;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
};
const TeamAccessControl: React.FC = () => {
  const $ = _c(83);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    user
  } = useContext(UserContext);
  const {
    team,
    mutateTeam,
    organization_role,
    team_role,
    resetContext,
    subscription
  } = useContext(DashboardContext);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const [show_all_inherited, setShowAllInherited] = useState(false);
  const is_organization_admin = organization_role === "admin" || organization_role === "owner";
  let t0;
  if ($[0] !== team.id) {
    t0 = APIMethodKeys.teams.listMembers(team.id);
    $[0] = team.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== HookdeckAPI.teams) {
    t1 = () => HookdeckAPI.teams.listMembers();
    $[2] = HookdeckAPI.teams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: members,
    mutate: mutateMembers
  } = useSWR(t0, t1);
  let t2;
  if ($[4] !== HookdeckAPI.teams || $[5] !== addToast || $[6] !== mutateMembers || $[7] !== mutateTeam) {
    t2 = v => {
      HookdeckAPI.teams.update({
        private: v.private
      }).then(team_0 => {
        mutateTeam(team_0);
        mutateMembers(undefined);
        addToast("success", `Project access is now ${v.private ? "private" : "public"} `);
      }).catch(() => {
        addToast("error", "Failed to update project, please contact us.");
      });
    };
    $[4] = HookdeckAPI.teams;
    $[5] = addToast;
    $[6] = mutateMembers;
    $[7] = mutateTeam;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const onSubmit = t2;
  let t3;
  if ($[9] !== HookdeckAPI.teams || $[10] !== addToast || $[11] !== mutateMembers) {
    t3 = async (id, role) => {
      ;
      try {
        await HookdeckAPI.teams.updateRole(id, role);
        mutateMembers(members_0 => members_0?.map(m => m.id === id ? {
          ...m,
          role
        } : m), false);
        addToast("success", `Role changed to ${capitalizeFirstLetter(role)}`);
      } catch (t4) {
        const err = t4;
        addToast("error", err.message);
      }
    };
    $[9] = HookdeckAPI.teams;
    $[10] = addToast;
    $[11] = mutateMembers;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const changeRole = t3;
  let t4;
  if ($[13] !== HookdeckAPI.teams || $[14] !== addToast || $[15] !== mutateMembers || $[16] !== showDialog) {
    t4 = (id_0, email) => {
      showDialog(async () => {
        ;
        try {
          await HookdeckAPI.teams.removeMember(id_0);
          mutateMembers(t6 => {
            let members_1 = t6 === undefined ? [] : t6;
            members_1 = members_1?.filter(m_0 => m_0.id !== id_0) ?? [];
            return members_1;
          }, false);
          addToast("success", "Member removed from organization");
        } catch (t5) {
          const err_0 = t5;
          addToast("error", err_0.message);
        }
      }, _temp, {
        title: "Remove from project",
        message: <Text><Text as="span" bold={true}>{email}</Text>{" "}will lose access to this project and all its data. Are you sure?</Text>,
        submit_label: "Remove from project",
        cancel_label: "Cancel",
        danger: true
      });
    };
    $[13] = HookdeckAPI.teams;
    $[14] = addToast;
    $[15] = mutateMembers;
    $[16] = showDialog;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleRemove = t4;
  let t5;
  if ($[18] !== HookdeckAPI.teams || $[19] !== addToast || $[20] !== resetContext || $[21] !== showDialog) {
    t5 = () => {
      showDialog(async () => {
        ;
        try {
          await HookdeckAPI.teams.leave();
          resetContext();
        } catch (t6) {
          const err_1 = t6;
          addToast("error", err_1.message);
        }
      }, _temp2, {
        title: "Leave project",
        message: <Text>You will lose access to this project and all of it's data. If you’d like to re-join, a project admin will need to re-add you. Are you sure?</Text>,
        submit_label: "Leave project",
        cancel_label: "Stay in project",
        danger: true
      });
    };
    $[18] = HookdeckAPI.teams;
    $[19] = addToast;
    $[20] = resetContext;
    $[21] = showDialog;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const handleLeave = t5;
  let T0;
  let t10;
  let t11;
  let t12;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[23] !== addToast || $[24] !== changeRole || $[25] !== handleLeave || $[26] !== handleRemove || $[27] !== is_organization_admin || $[28] !== members || $[29] !== mutateMembers || $[30] !== onSubmit || $[31] !== show_all_inherited || $[32] !== subscription?.features || $[33] !== team.directory_sync_enabled || $[34] !== team.private || $[35] !== team_role || $[36] !== user) {
    const inherited_members = members?.filter(_temp3);
    const managed_members = members?.filter(_temp4);
    if ($[45] !== addToast || $[46] !== members || $[47] !== mutateMembers) {
      t12 = <Route path="/settings/project/access/add" render={() => members && <AddTeamMemberModal members={members} onMembersAdded={new_members => {
        mutateMembers(members_2 => {
          new_members.forEach(member => {
            if (!members_2?.some(m_3 => m_3.user_id === member.user_id)) {
              members_2 = [member, ...(members_2 ?? [])];
            }
          });
          return members_2;
        }, false);
        addToast("success", "Member access granted added to project");
      }} />} />;
      $[45] = addToast;
      $[46] = members;
      $[47] = mutateMembers;
      $[48] = t12;
    } else {
      t12 = $[48];
    }
    T0 = Container;
    t6 = true;
    t7 = true;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        b: 10
      };
      $[49] = t8;
    } else {
      t8 = $[49];
    }
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Text bold={true} size="l" as="h2" m={{
        t: 0,
        b: 4
      }}>Project access</Text>;
      $[50] = t9;
    } else {
      t9 = $[50];
    }
    let t13;
    if ($[51] !== team.private) {
      t13 = {
        private: team.private
      };
      $[51] = team.private;
      $[52] = t13;
    } else {
      t13 = $[52];
    }
    let t14;
    let t15;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = {
        x: 4,
        y: 3
      };
      t15 = {
        justify: "flex-start",
        align: "center",
        gap: 3
      };
      $[53] = t14;
      $[54] = t15;
    } else {
      t14 = $[53];
      t15 = $[54];
    }
    const t16 = !is_organization_admin;
    let t17;
    if ($[55] !== t16) {
      t17 = <SwitchInput name="private" disabled={t16} />;
      $[55] = t16;
      $[56] = t17;
    } else {
      t17 = $[56];
    }
    let t18;
    if ($[57] !== is_organization_admin || $[58] !== t17) {
      t18 = <Tooltip disabled={is_organization_admin} tooltip="Only organization admins and owners can change project privacy.">{t17}</Tooltip>;
      $[57] = is_organization_admin;
      $[58] = t17;
      $[59] = t18;
    } else {
      t18 = $[59];
    }
    let t19;
    let t20;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = {
        align: "center",
        gap: 1
      };
      t20 = <Text>Private project</Text>;
      $[60] = t19;
      $[61] = t20;
    } else {
      t19 = $[60];
      t20 = $[61];
    }
    let t21;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <Div flex={t19}>{t20}<Tooltip tooltip="Private projects are accessible to organization admins and with members with project access."><Icon muted={true} icon="info" /></Tooltip></Div>;
      $[62] = t21;
    } else {
      t21 = $[62];
    }
    let t22;
    if ($[63] !== t18) {
      t22 = <StyledCard p={t14} flex={t15}>{t18}{t21}</StyledCard>;
      $[63] = t18;
      $[64] = t22;
    } else {
      t22 = $[64];
    }
    let t23;
    if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <SubmitOnchange />;
      $[65] = t23;
    } else {
      t23 = $[65];
    }
    let t24;
    if ($[66] !== t22) {
      t24 = <Form>{t22}{t23}</Form>;
      $[66] = t22;
      $[67] = t24;
    } else {
      t24 = $[67];
    }
    if ($[68] !== onSubmit || $[69] !== t13 || $[70] !== t24) {
      t10 = <Formik initialValues={t13} onSubmit={onSubmit}>{t24}</Formik>;
      $[68] = onSubmit;
      $[69] = t13;
      $[70] = t24;
      $[71] = t10;
    } else {
      t10 = $[71];
    }
    t11 = team?.private && <><Div m={{
        b: 4,
        t: 14
      }}><Text bold={true} size="l" as="h2" m={0}>Members with inherited access</Text><Text muted={true}>Organization owners and admins inherit access to all projects.</Text></Div><StyledCard>{inherited_members?.sort(_temp5).filter((_, index) => show_all_inherited ? true : index < 3).map(member_0 => <StyledCardSection p={{
          x: 4,
          y: 3
        }} flex={{
          align: "center",
          gap: 4
        }} key={member_0.id}><Div w={50}><Text semi={true} ellipsis={true}>{member_0.user_name}{member_0.user_id === user.id && " (You)"}</Text><Text muted={true} ellipsis={true}>{member_0.user_email}</Text></Div><Div w={50}><Button outline={true} disabled={true}>{capitalizeFirstLetter(member_0.role)}</Button></Div><Div w={{
            px: 32
          }} /></StyledCardSection>)}{inherited_members && inherited_members.length > 3 && <StyledCardSection p={{
          x: 4,
          y: 2
        }}><Link icon={show_all_inherited ? "collapse_all" : "expand_all"} onClick={() => setShowAllInherited(!show_all_inherited)}>{!show_all_inherited ? `View all ${inherited_members.length}` : "Collapse"}</Link></StyledCardSection>}{inherited_members && inherited_members.length === 0 && <Text muted={true} p={4}><Icon muted={true} icon="info" left={true} />There are no inherited members with project access</Text>}</StyledCard><Div m={{
        b: 4,
        t: 14
      }} flex={{
        justify: "space-between",
        align: "center"
      }}><Div><Text bold={true} size="l" as="h2" m={0}>Members with project access</Text><Text muted={true}>Organization members can create or be added to private projects.</Text></Div>{!team?.directory_sync_enabled && <WithTeamAdminRole><Button to={_temp6} primary={true} icon="add">Add members</Button></WithTeamAdminRole>}</Div><StyledCard>{managed_members && managed_members.length === 0 && <Text center={true} muted={true} p={6.5}>There are no members with project access.</Text>}{managed_members?.sort(_temp7).map((member_1, i) => <StyledCardSection p={{
          x: 4,
          y: 3
        }} flex={{
          align: "center",
          gap: 4
        }} key={member_1.id}><Div w={50}><Text semi={true} ellipsis={true}>{member_1.user_name}{member_1.user_id === user.id && " (You)"}</Text><Text muted={true} ellipsis={true}>{member_1.user_email}</Text></Div><Div w={50}>{user.id !== member_1.user_id && team_levels_by_role[team_role] >= team_levels_by_role.admin ? <DropdownMenu placement="bottom-start" outline={true} w={{
              px: 272
            }} label={`Project ${capitalizeFirstLetter(member_1.role)}`} options={createTeamRoleOptions(!!subscription?.features?.includes("viewer_role")).map(option => ({
              ...option,
              onClick: () => changeRole(member_1.id as string, option.value)
            }))} /> : <Button outline={true} disabled={true}>{capitalizeFirstLetter(member_1.role)}</Button>}</Div><Div w={{
            px: 32
          }}>{user.id === member_1.user_id && !user.workos_org_id && <Div flex={{
              justify: "flex-end"
            }}><DropdownMenu placement="bottom-end" ghost={true} icon="horizontal_more" options={[{
                icon: "leave",
                label: "Leave Project",
                onClick: handleLeave,
                danger: true
              }]} /></Div>}{user.id !== member_1.user_id && <WithTeamAdminRole><Button ghost={true} icon="delete" onClick={() => handleRemove(member_1.id as string, member_1.user_email)} /></WithTeamAdminRole>}</Div></StyledCardSection>)}</StyledCard></>;
    $[23] = addToast;
    $[24] = changeRole;
    $[25] = handleLeave;
    $[26] = handleRemove;
    $[27] = is_organization_admin;
    $[28] = members;
    $[29] = mutateMembers;
    $[30] = onSubmit;
    $[31] = show_all_inherited;
    $[32] = subscription?.features;
    $[33] = team.directory_sync_enabled;
    $[34] = team.private;
    $[35] = team_role;
    $[36] = user;
    $[37] = T0;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t6;
    $[42] = t7;
    $[43] = t8;
    $[44] = t9;
  } else {
    T0 = $[37];
    t10 = $[38];
    t11 = $[39];
    t12 = $[40];
    t6 = $[41];
    t7 = $[42];
    t8 = $[43];
    t9 = $[44];
  }
  let t13;
  if ($[72] !== T0 || $[73] !== t10 || $[74] !== t11 || $[75] !== t6 || $[76] !== t7 || $[77] !== t8 || $[78] !== t9) {
    t13 = <T0 large={t6} left={t7} m={t8}>{t9}{t10}{t11}</T0>;
    $[72] = T0;
    $[73] = t10;
    $[74] = t11;
    $[75] = t6;
    $[76] = t7;
    $[77] = t8;
    $[78] = t9;
    $[79] = t13;
  } else {
    t13 = $[79];
  }
  let t14;
  if ($[80] !== t12 || $[81] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[80] = t12;
    $[81] = t13;
    $[82] = t14;
  } else {
    t14 = $[82];
  }
  return t14;
};
export default TeamAccessControl;
function _temp() {
  return null;
}
function _temp2() {
  return null;
}
function _temp3(m_1) {
  return m_1.inherited;
}
function _temp4(m_2) {
  return !m_2.inherited;
}
function _temp5(a, b) {
  return a.created_at > b.created_at ? -1 : 1;
}
function _temp6(location) {
  return {
    ...location,
    pathname: "/settings/project/access/add",
    state: {
      scroll: false
    }
  };
}
function _temp7(a_0, b_0) {
  return a_0.created_at > b_0.created_at ? -1 : 1;
}